import { useState } from "react";
import { salaryOn } from "@dentacore/shared-types";
import type { SalaryPerson, SaveSalary } from "@dentacore/shared-types";

import { parseMoney } from "../patients/money";
import { Modal } from "../ui/modal";
import { NumberField } from "../ui/number-field";
import {
  amountProblem,
  firstOfNextMonth,
  money,
  moneyInput,
  monthName,
  niceDay,
  parseDay,
} from "./format";
import styles from "./accounting.module.css";
import { RequiredMark } from "../ui/required-mark";

/**
 * *Change salary* — a new monthly figure from a date (AC-D10).
 *
 * The old figure is kept, never overwritten: months before the date keep it,
 * which is what lets last year's profit stay what it was. **The date defaults
 * to the first of next month**, the common case; a date in an earlier month is
 * allowed — a raise agreed late — and the dialog says plainly that profit from
 * that month on is worked out again.
 */
export function SalaryDialog({
  person,
  today,
  currency,
  busy,
  onSave,
  onClose,
}: {
  person: SalaryPerson;
  today: string;
  currency: string;
  busy: boolean;
  /** Resolves true once the salary is saved, so the dialog closes only then. */
  onSave: (body: SaveSalary) => Promise<boolean>;
  onClose: () => void;
}): React.JSX.Element {
  const now = salaryOn(person.salaries, today);
  const [amount, setAmount] = useState(now === null ? "" : moneyInput(now.monthlyCents, currency));
  const [from, setFrom] = useState(firstOfNextMonth(today));

  const amountError = amountProblem(amount, currency, { zeroAllowed: true });
  const thisMonth = `${today.slice(0, 7)}-01`;
  const dateError =
    from === ""
      ? "Choose the date it takes effect."
      : person.startedOn !== null && from < person.startedOn
        ? `That is before ${person.name} started (${niceDay(person.startedOn)}).`
        : null;
  const blocked = amountError !== null || dateError !== null;
  // A date before this month changes months already counted.
  const recounts = from !== "" && from < thisMonth;

  return (
    <Modal label={`Change ${person.name}'s salary`} onClose={onClose} testId="salary-dialog">
      <form
        className={styles.dialogForm}
        onSubmit={(event) => {
          event.preventDefault();
          // Zero is a salary too: unpaid time (QF-D29). parseMoney refuses it.
          const cents = Number(amount.trim()) === 0 ? 0 : parseMoney(amount, currency);
          if (blocked || cents === null) return;
          void onSave({ effectiveFrom: from, monthlyCents: cents }).then((landed) => {
            if (landed) onClose();
          });
        }}
      >
        <h2 className={styles.dialogTitle}>Change {person.name}&rsquo;s salary</h2>
        {now !== null && (
          <p className={styles.dialogSub}>
            Now {money(now.monthlyCents, currency)} a month, since {niceDay(now.effectiveFrom)}.
          </p>
        )}

        <div className={styles.dialogGrid}>
          <NumberField
            label="New monthly salary"
            required
            className={styles.field}
            labelClassName={styles.fieldLabel}
            inputClassName={styles.input}
            value={amount}
            onChange={setAmount}
            rules={{ min: 0 }}
            autoFocus
            testId="salary-amount"
          />
          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              Takes effect from
              <RequiredMark />
            </span>
            <input
              type="date"
              className={styles.input}
              value={from}
              min={person.startedOn ?? undefined}
              onChange={(event) => setFrom(event.target.value)}
              data-testid="salary-from"
            />
          </label>
        </div>

        <p
          className={recounts ? `${styles.note} ${styles.warmNote}` : styles.note}
          data-testid="salary-from-note"
        >
          {recounts
            ? `This date is in an earlier month, so profit from ${monthName(parseDay(from).month)} ${String(parseDay(from).year)} on is worked out again.`
            : "Months before this date keep the old salary."}
        </p>

        {amount.trim() !== "" && amountError !== null && (
          <p className={styles.error}>{amountError}</p>
        )}
        {dateError !== null && <p className={styles.error}>{dateError}</p>}

        <div className={styles.dialogActions}>
          <button type="button" className={styles.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className={styles.primary}
            disabled={busy || blocked}
            data-testid="salary-save"
          >
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * *Last day* — when a person's salary stops counting (AC-D18). Their days after
 * it cost nothing; the days up to it still count (AC-D11). A person who is
 * staying after all has it cleared, which counts their salary again from then
 * on.
 */
export function LastDayDialog({
  person,
  today,
  busy,
  onSave,
  onClose,
}: {
  person: SalaryPerson;
  today: string;
  busy: boolean;
  /** `null` clears it. Resolves true once saved. */
  onSave: (endedOn: string | null) => Promise<boolean>;
  onClose: () => void;
}): React.JSX.Element {
  const [date, setDate] = useState(person.endedOn ?? today);

  const error =
    date === ""
      ? "Choose a date."
      : person.startedOn !== null && date < person.startedOn
        ? `That is before ${person.name} started (${niceDay(person.startedOn)}).`
        : null;

  const save = (endedOn: string | null): void => {
    void onSave(endedOn).then((landed) => {
      if (landed) onClose();
    });
  };

  return (
    <Modal label={`${person.name}'s last day`} onClose={onClose} testId="last-day-dialog">
      <form
        className={styles.dialogForm}
        onSubmit={(event) => {
          event.preventDefault();
          if (error === null) save(date);
        }}
      >
        <h2 className={styles.dialogTitle}>{person.name}&rsquo;s last day</h2>
        <p className={styles.dialogSub}>Their salary stops counting after this day.</p>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>
            Last day worked
            <RequiredMark />
          </span>
          <input
            type="date"
            className={styles.input}
            value={date}
            min={person.startedOn ?? undefined}
            onChange={(event) => setDate(event.target.value)}
            autoFocus
            data-testid="last-day-date"
          />
        </label>

        {error !== null && <p className={styles.error}>{error}</p>}

        <div className={styles.dialogActions}>
          {person.endedOn !== null && (
            <button
              type="button"
              className={`${styles.quiet} ${styles.pushLeft}`}
              disabled={busy}
              onClick={() => save(null)}
              data-testid="last-day-clear"
            >
              Still here — remove the last day
            </button>
          )}
          <button type="button" className={styles.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className={styles.primary}
            disabled={busy || error !== null}
            data-testid="last-day-save"
          >
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}
